// @ts-strict-ignore
import type {Axis, Chart, Point, Series} from 'highcharts';

/**
 * Highcharts v3 positioned the tooltip to the left of the point. Highcharts v6 changed the tooltip position to be
 * above the point. We want the old Highcharts v3 behavior, so I've extracted the Highcharts v3 tooltip positioning
 * logic from https://github.com/mixpanel/analytics/blob/336968c3a4d8bcde43ad534e250f8f4921bc21c0/media/plugins/highcharts/highcharts.src.es5.js#L8899-L8944
 */
export function getTooltipPosition({
  tooltipWidth,
  tooltipHeight,
  pointX,
  pointY,
  chartLeft,
  chartTop,
  chartWidth,
  chartHeight,
}) {
  const distance = 12;

  pointX = isNaN(pointX) ? 0 : pointX;
  let x = pointX + chartLeft - tooltipWidth - distance;
  let y = pointY - tooltipHeight + chartTop + 15; // 15 means the point is 15 pixels up from the bottom of the tooltip
  let alignedRight = false;

  // It is too far to the left, adjust it
  if (x < 7) {
    x = chartLeft + Math.max(pointX, 0) + distance;
  }

  // Test to see if the tooltip is too far to the right,
  // if it is, move it back to be inside and then up to not cover the point.
  if (x + tooltipWidth > chartLeft + chartWidth) {
    x -= x + tooltipWidth - (chartLeft + chartWidth);
    y = pointY - tooltipHeight + chartTop - distance;
    alignedRight = true;
  }

  // If it is now above the plot area, align it to the top of the plot area
  if (y < chartTop + 5) {
    y = chartTop + 5;

    // If the tooltip is still covering the point, move it below instead
    if (alignedRight && pointY >= y && pointY <= y + tooltipHeight) {
      y = pointY + chartTop + distance; // below
    }
  }

  // Now if the tooltip is below the chart, move it up. It's better to cover the
  // point than to disappear outside the chart. #834.
  if (y + tooltipHeight > chartTop + chartHeight) {
    y = Math.max(chartTop, chartTop + chartHeight - tooltipHeight - distance); // below
  }

  return {x, y};
}

/** Simulate user hover by updating series' states  */
export function hoverSeries(series: Series | Series[]) {
  series = Array.isArray(series) ? series : [series];
  const seriesNames = new Set(series.map((s) => s.name));
  // sets all series to an inactive state except for the
  // ones being higlighted, this prevents flickering of currently hovered series
  series[0].chart.series.forEach((s: Series) => !seriesNames.has(s.name) && s.setState(`inactive`));
  series.forEach((s: Series) => s.setState(`hover`));
  // @ts-ignore unfortunately highcharts.d.ts does not include Series.linkedSeries
  series.linkedSeries?.[0]?.setState(`hover`);
  // @ts-ignore unfortunately highcharts.d.ts does not include Series.linkedParent
  series.linkedParent?.setState(`hover`);
}

/** Reset series' states after `hoverSeries` */
export function unHoverChart(chart: Chart) {
  chart.series.forEach((s) => s.setState(``));
}

/** Selects a highcharts point and its series, and disables chart interactions. Used with popovers. */
export function selectPoint(point?: Point) {
  if (!point) {
    return;
  }

  const {series} = point;
  series.chart.container.style.pointerEvents = `none`;

  point.select(true);
  // foreground the selected series
  series.chart.series.forEach((s) => {
    const zIndex = s.index === series.index ? 2 : 1;
    // @ts-ignore update allows partial update
    s.update({zIndex}, false);
  });
  // Note: this redraw must precede our updates to the series' states below, otherwise they are overwritten because the
  // chart configuration's `plotOptions.line.states.normal.animation.duration` option is set to 25ms. Redraw apparently
  // cancels the pending change to `normal` state.
  series.chart.redraw();
  // update series' states so they continue to look hovered
  hoverSeries(series);

  series.chart.tooltip.destroy();
  series.chart.tooltip.update({enabled: false});
}

/** Deselects a highcharts point and its series, and re-enables chart interactions. Used when a popover is closed. */
export function deselectPoint(point?: Point) {
  if (!point) {
    return;
  }

  point.select(false);
  unHoverChart(point.series.chart);

  point.series.chart.tooltip.update({enabled: true});

  point.series.chart.container.style.pointerEvents = ``;
}

// region Highcharts overrides

const MIN_LOG_ADJUSTMENT_THRESHOLD = 10;

export function logarithmicToLinear(num) {
  const isNegative = num < 0;
  let adjustedNum = Math.abs(num);
  if (adjustedNum < MIN_LOG_ADJUSTMENT_THRESHOLD) {
    adjustedNum += (MIN_LOG_ADJUSTMENT_THRESHOLD - adjustedNum) / MIN_LOG_ADJUSTMENT_THRESHOLD;
  }
  const result = Math.log(adjustedNum) / Math.LN10;
  return isNegative ? -result : result;
}

export function linearToLogarithmic(num) {
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  let result = Math.pow(10, absNum);
  if (result < MIN_LOG_ADJUSTMENT_THRESHOLD) {
    result = (MIN_LOG_ADJUSTMENT_THRESHOLD * (result - 1)) / (MIN_LOG_ADJUSTMENT_THRESHOLD - 1);
  }
  return isNegative ? -result : result;
}

// Based on https://jsfiddle.net/BlackLabel/23gwcadf/ from https://www.highcharts.com/forum/viewtopic.php?f=9&t=48520
// eslint-disable-next-line @typescript-eslint/naming-convention
export function patchHighchartsForNegativeLog(Highcharts) {
  Highcharts.addEvent(Highcharts.Axis, `afterInit`, function (this: Axis) {
    // @ts-ignore this property is not included in highcharts.d.ts :(
    const logarithmic = this.logarithmic;
    if (logarithmic) {
      // Pass error messages
      // @ts-ignore this property is not included in highcharts.d.ts :(
      this.positiveValuesOnly = false;

      // Override conversions
      logarithmic.log2lin = function (num) {
        return logarithmicToLinear(num);
      };

      logarithmic.lin2log = function (num) {
        return linearToLogarithmic(num);
      };
    }
  });
}

// endregion
